/**
 * cutscene-data.ts — WEB.4.CS — typed accessors over data/cutscenes.json.
 *
 * The JSON is generated from `GDG/Cutscene-Script-And-Prompts.md`; do NOT
 * hand-edit narration / titles here. Edit the markdown and re-run the
 * extractor (see commit message of the cutscene framework PR).
 *
 * Frame image assets are NOT in the repo yet — the player gracefully falls
 * back to narration-only over an era-palette gradient when `imgSrc` 404s.
 */

import data from "../../../../data/cutscenes.json";

export type CutsceneGroupId =
  | "intro"
  | "transition-1"
  | "transition-2"
  | "transition-3"
  | "ending-A"
  | "ending-B"
  | "ending-C"
  | "ending-D";

export interface CutsceneFrame {
  /** Stable id, e.g. "intro-1", "transition-1-T1.1", "ending-A-A1". */
  id: string;
  /** 1 = intro, 2 = era transition, 3 = ending. */
  tier: 1 | 2 | 3;
  /** Short human-readable title for dev / a11y label. */
  title: string;
  /** Voiceover-style narration text rendered as an HTML caption. */
  narration: string;
  /** Auto-advance delay in milliseconds. */
  durationMs: number;
  /** Relative URL under /cutscenes/. May 404 — the player falls back. */
  imgSrc: string;
}

export interface CutsceneGroup {
  id: CutsceneGroupId;
  tier: 1 | 2 | 3;
  /** Suggested BGM channel — "intro" / "victory" / "defeat" / "menu" / "era-transition". */
  bgmTrack: string | null;
  /** Ordered frame ids. */
  frameIds: string[];
}

interface RawDoc {
  _meta: { version: number; frameCount: number; groupCount: number; comment: string };
  groups: CutsceneGroup[];
  frames: CutsceneFrame[];
}

const _doc = data as unknown as RawDoc;
const _frameById = new Map<string, CutsceneFrame>(_doc.frames.map((f) => [f.id, f]));
const _groupById = new Map<CutsceneGroupId, CutsceneGroup>(_doc.groups.map((g) => [g.id, g]));

export function getCutsceneGroup(id: CutsceneGroupId): CutsceneGroup | undefined {
  return _groupById.get(id);
}

export function getCutsceneFrame(id: string): CutsceneFrame | undefined {
  return _frameById.get(id);
}

/** Total frame count across all groups (exported for tests). */
export const CUTSCENE_FRAME_COUNT: number = _doc.frames.length;
export const CUTSCENE_GROUP_COUNT: number = _doc.groups.length;

/**
 * Map a group id to a coarse era palette (used by the fallback gradient
 * when imgSrc fails). Mirrors the doc's era palette table.
 */
export function paletteForGroup(groupId: CutsceneGroupId): {
  bg: string;
  fg: string;
} {
  switch (groupId) {
    case "intro":
      // Earth-to-dawn warm parchment.
      return { bg: "#1d1108", fg: "#f0d8a0" };
    case "transition-1":
      // Era 1 → 2 (sepia/rust → amber).
      return { bg: "#3a1c0a", fg: "#e8a050" };
    case "transition-2":
      // Era 2 → 3 (amber/smog → chrome-blue).
      return { bg: "#1f2a3a", fg: "#a0c0e0" };
    case "transition-3":
      // Era 3 → 4 (chrome → solar-gold).
      return { bg: "#1c2418", fg: "#ffd86b" };
    case "ending-A":
      // Renewable victory (solar-gold + leaf-green).
      return { bg: "#0f2818", fg: "#5bc07a" };
    case "ending-B":
      // Economic dominance (gold + cold steel — ambiguous).
      return { bg: "#1a1a22", fg: "#c9974a" };
    case "ending-C":
      // Climate collapse (heavier sky, embers).
      return { bg: "#2a0a0a", fg: "#ea5656" };
    case "ending-D":
      // Mutual destruction (cold ash).
      return { bg: "#161616", fg: "#9a9a9a" };
    default:
      return { bg: "#1d1108", fg: "#f0d8a0" };
  }
}
